// A new version after a deploy (D-123): an open tab keeps running the build it loaded until it reloads, so it asks the
// server which web build it hands out (`/api/health` `build`) when the window comes back into view, when the live
// connection comes back, and every five minutes. When they differ: at a table a quiet line says the page reloads when
// the round ends, and it does so on leaving the table; anywhere else a toast offers Reload, and the next screen change
// reloads by itself. Never mid-round.
import { Icon } from "./Icon";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { useLocation } from "react-router-dom";
import { BUILD } from "../lib/telemetry";
import { onSocketState } from "../lib/ws";
import "./update-prompt.css";

export const UPDATE_CHECK_MS = 5 * 60_000;
const AT_TABLE = /^\/table(\/|$)/;

/** Indirection so tests can watch the reload instead of performing it. */
export const page = { reload: (): void => { window.location.reload(); } };

/** The build the server hands out now, or null when it doesn't say (development) or can't be reached. */
export async function serverBuild(): Promise<string | null> {
  try {
    const r = await fetch("/api/health", { cache: "no-store" });
    if (!r.ok) return null;
    const j = (await r.json()) as { build?: unknown };
    return typeof j.build === "string" ? j.build : null;
  } catch { return null; }
}

/** True once the server hands out a different build from the one this page runs. */
export function useNewVersion(own: string = BUILD): boolean {
  const [stale, setStale] = useState(false);
  const check = useCallback(async () => {
    if (own === "dev") return;
    const b = await serverBuild();
    if (b && b !== own) setStale(true);
  }, [own]);
  useEffect(() => {
    if (stale) return;
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    let wasOpen = true;
    const offState = onSocketState((s) => { if (s === "open" && !wasOpen) void check(); wasOpen = s === "open"; });
    const t = window.setInterval(() => { void check(); }, UPDATE_CHECK_MS);
    return () => { window.removeEventListener("focus", onVisible); document.removeEventListener("visibilitychange", onVisible); offState(); window.clearInterval(t); };
  }, [check, stale]);
  return stale;
}

/** How long the table's line and the toast stay up before stepping aside. */
export const LINE_MS = 6_000;
export const TOAST_MS = 12_000;

/** Where an element's text ends, not its box (a header title's box stretches to the buttons). */
function textRect(el: Element | null): DOMRect | undefined {
  if (!el) return undefined;
  try {
    // The text itself: a range over the block would measure the block.
    const text = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (n.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP) }).nextNode();
    if (text) {
      const r = document.createRange();
      r.selectNodeContents(text);
      const b = r.getBoundingClientRect();
      if (b.width) return b;
    }
  } catch { /* no layout (tests) */ }
  return el.getBoundingClientRect();
}

/**
 * Where the table's line goes, so it never sits over the round title or the turn timer (D-127): just under the header
 * when the phone is upright, and in the open space under the hand when the hand sits beside the table (a phone on its
 * side). In the waiting room it goes beside the table's name when there is room, else just above the buttons (D-129).
 * Anywhere else (desktop) it keeps its place at the top.
 */
function useLineSpot(on: boolean): CSSProperties | undefined {
  const [spot, setSpot] = useState<CSSProperties>();
  useLayoutEffect(() => {
    if (!on) return;
    const rect = (sel: string) => document.querySelector(sel)?.getBoundingClientRect();
    let last = "";
    const place = () => {
      const header = rect(".pl-header"), arena = rect(".pl-arena"), hand = rect(".pl-hand-area"), sort = rect('[data-testid="sort-pill"]');
      let next: CSSProperties | undefined;
      if (arena && hand && hand.left >= arena.right - 1) next = { top: Math.round(Math.max(hand.bottom, sort?.bottom ?? 0) + 8), left: Math.round(hand.left + hand.width / 2) };
      else if (arena && header && header.bottom <= arena.top + 1) next = { top: Math.round(header.bottom + 4) };
      else if (!arena) {
        // The waiting room (D-129): beside the table's name when the header has room (sideways, desktop), otherwise just
        // above the buttons. Not under the header, where it sat on the Players and Table rules headings.
        const lobbyHeader = rect('[data-testid^="lobby"] .pl-header'), title = textRect(document.querySelector('[data-testid^="lobby"] .pl-header-title'));
        const buttons = rect('[data-testid^="lobby"] .pl-lobby-actions'), line = rect('[data-testid="update-line"]');
        const w = line?.width || 320, h = line?.height || 30;
        const end = rect('[data-testid^="lobby"] .pl-header-actions')?.left ?? lobbyHeader?.right ?? 0;
        if (lobbyHeader && title && end - title.right >= w + 24) next = { top: Math.round(title.top + title.height / 2 - h / 2), left: Math.round((title.right + end) / 2) };
        else if (buttons) next = { top: Math.round(Math.min(buttons.top, window.innerHeight) - h - 8), left: Math.round(buttons.left + buttons.width / 2) };
      }
      const key = JSON.stringify(next ?? null);
      if (key !== last) { last = key; setSpot(next); }
    };
    place();
    // A reload finds the new version as its connection opens, before the table has drawn, so place again as it does.
    const seen = new MutationObserver(place);
    seen.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", place);
    return () => { seen.disconnect(); window.removeEventListener("resize", place); };
  }, [on]);
  return spot;
}

export function UpdatePrompt({ own }: { own?: string }): ReactElement | null {
  const stale = useNewVersion(own);
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [lineGone, setLineGone] = useState(false);
  const seenAt = useRef<string | null>(null);
  const atTable = AT_TABLE.test(pathname);
  const spot = useLineSpot(stale && atTable && !lineGone);
  // Both notices step aside after a while so they never sit over the table's title or a screen's buttons; the reload
  // on the next screen change still happens (D-123 follow-up).
  useEffect(() => {
    if (!stale) return;
    const t = window.setTimeout(() => (atTable ? setLineGone(true) : setDismissed(true)), atTable ? LINE_MS : TOAST_MS);
    return () => window.clearTimeout(t);
  }, [stale, atTable]);
  // Once a new version is known, the next screen change reloads, unless it goes to a table (a deal moving the waiting
  // room to the table): there it waits until the player leaves the table.
  useEffect(() => {
    if (!stale) return;
    if (seenAt.current === null) { seenAt.current = pathname; return; }
    if (pathname !== seenAt.current && !atTable) page.reload();
    seenAt.current = pathname;
  }, [stale, pathname, atTable]);
  if (!stale) return null;
  if (atTable) {
    if (lineGone) return null;
    return <div className="update-line" style={spot} role="status" data-testid="update-line">DIGU was updated. It reloads when this round ends.</div>;
  }
  if (dismissed) return null;
  return (
    <div className="update-toast" role="status" data-testid="update-toast">
      <span className="update-toast__text">DIGU was updated.</span>
      <button type="button" className="update-toast__reload" onClick={() => page.reload()} data-testid="update-reload">Reload</button>
      <button type="button" className="update-toast__x" onClick={() => setDismissed(true)} aria-label="Not now" data-testid="update-dismiss"><Icon name="x" size={18} /></button>
    </div>
  );
}
